import type {
  VehicleMap,
  VehicleMapTableProps
} from "@dealer-center/inventory-pages-gvm-interface";
import type {
  InterstateOnChangeEvent,
  SelectInputEventValue,
  TypeSafeTableColumns
} from "@dealer-center/core-components";
import { Button, Table, TextInput } from "@dealer-center/core-components";
import React, { useState } from "react";
import {
  useVehicleMapDelete,
  useVehicleMapSave
} from "@dealer-center/inventory-pages-gvm-fetch";
import { useIsGvmRwAccessUser } from "../../util/gvmUserUtil";

const defaultVehicleMap: VehicleMap = {
  recordId: "",
  thirdPartyId: "",
  globalVehicleId: "",
  thirdPartyVehicleId: "",
  manuallyCreated: true,
  active: true
};

function getVehicleColumns(
  onDelete: (record: VehicleMap) => void,
  isGvmRwAccessUser: boolean
): TypeSafeTableColumns<VehicleMap> {
  return [
    {
      key: "thirdPartyId",
      dataIndex: "thirdPartyId",
      title: "Third Party ID"
    },
    {
      key: "thirdPartyVehicleId",
      dataIndex: "thirdPartyVehicleId",
      title: "Third Party Vehicle ID"
    },
    {
      key: "manuallyCreated",
      dataIndex: "manuallyCreated",
      title: "Manually Created",
      render: (value: boolean) => (value ? "Yes" : "No")
    },
    {
      key: "action",
      title: "Action",
      render: (_, record) =>
        isGvmRwAccessUser &&
        record.manuallyCreated && (
          <span
            style={{
              cursor: "pointer",
              color: "blue"
            }}
            title="Delete"
            onClick={() => onDelete(record)}
          >
            Delete
          </span>
        )
    }
  ];
}

export const VehicleMapTable = ({
  isLoading,
  vehicleId,
  vehicleMapList
}: VehicleMapTableProps) => {
  const [vehicleMap, setVehicleMap] = useState<VehicleMap>(defaultVehicleMap);
  const saveVehicleMapMutation = useVehicleMapSave();
  const deleteVehicleMapMutation = useVehicleMapDelete();
  const isGvmRwAccessUser = useIsGvmRwAccessUser();

  const isElementDisabled =
    !isGvmRwAccessUser ||
    saveVehicleMapMutation.isPending ||
    deleteVehicleMapMutation.isPending;

  const handleInputChange = (
    event: InterstateOnChangeEvent<SelectInputEventValue>
  ) => {
    const name = event.target.name || "";
    const value = event.target.value;

    setVehicleMap({ ...vehicleMap, [name]: value });
  };

  const handleVehicleMapDelete = (record: VehicleMap) => {
    deleteVehicleMapMutation.mutate(record.recordId);
  };

  const handleVehicleMapSave = () => {
    saveVehicleMapMutation.mutate({
      ...vehicleMap,
      globalVehicleId: vehicleId
    });
    setVehicleMap(defaultVehicleMap);
  };

  return (
    <>
      <Table.Table
        columns={getVehicleColumns(handleVehicleMapDelete, isGvmRwAccessUser)}
        data={vehicleMapList}
        data-testid="gvm-vehicle-map-results"
        dataDensity="small"
        emptyText="No mapping available"
        enablePagination={false}
        isLoading={isLoading}
        sortableColumns={true}
        tableLayout="auto"
      />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: "10px",
          marginBottom: "20px"
        }}
      >
        <div style={{ flex: "1", marginRight: "10px" }}>
          <TextInput
            disabled={isElementDisabled}
            name="thirdPartyId"
            placeholder="Third Party Id"
            type="text"
            value={vehicleMap.thirdPartyId}
            onChange={handleInputChange}
          />
        </div>
        <div style={{ flex: "1", marginRight: "10px" }}>
          <TextInput
            disabled={isElementDisabled}
            name="thirdPartyVehicleId"
            placeholder="Third Party Vehicle Id"
            type="text"
            value={vehicleMap.thirdPartyVehicleId}
            onChange={handleInputChange}
          />
        </div>

        <Button
          analyticsConfiguration={{
            eventName: "Add Vehicle Map button clicked"
          }}
          buttonStyle="secondary"
          disabled={isElementDisabled}
          type="button"
          onClick={handleVehicleMapSave}
        >
          {saveVehicleMapMutation.isPending ? "Adding..." : "Add Vehicle Map"}
        </Button>
      </div>
    </>
  );
};
